/**
 * FRONT-2473: Configuration → "While building" — what EDDY may use while it
 * builds this project. One master switch over the three groups (on selects
 * all of them), then each group on its own. Replaces the three checkmarks
 * that sat in the session's "…" menu (FRONT-2219). A change applies from the
 * builder's next turn: a run's token is minted with the set it started with.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { BookOpenTextIcon, GlobeIcon, LightningIcon, UsersThreeIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { CapabilityCard } from '~/features/apps/capabilities-section';
import { useSetCodingCapability, type CodingCapability } from '~/hooks/mutations/use-coding-mutations';
import {
  BUILDER_CAPABILITIES,
  builderCapsFrom,
  masterOn,
  masterStatus,
  masterToggle,
  type BuilderCaps,
} from './builder-capabilities-model';

const ICONS: Record<CodingCapability, ReactNode> = {
  web: <GlobeIcon size={18} />,
  knowledge: <BookOpenTextIcon size={18} />,
  agents: <UsersThreeIcon size={18} />,
};

export function BuilderCapabilities({
  session,
}: {
  session: { id: string; webToolsEnabled?: boolean; knowledgeToolsEnabled?: boolean; agentToolsEnabled?: boolean };
}) {
  const setCapability = useSetCodingCapability();
  const [caps, setCaps] = useState<BuilderCaps>(() => builderCapsFrom(session));
  useEffect(() => {
    setCaps(builderCapsFrom(session));
  }, [session.webToolsEnabled, session.knowledgeToolsEnabled, session.agentToolsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optimistic, reverted on failure — the server still gates what a run may call.
  const save = (next: BuilderCaps) => {
    const before = caps;
    const changes: Partial<BuilderCaps> = {};
    for (const { key } of BUILDER_CAPABILITIES) if (next[key] !== before[key]) changes[key] = next[key];
    if (Object.keys(changes).length === 0) return;
    setCaps(next);
    setCapability.mutate(
      { sessionId: session.id, changes },
      {
        onError: (e) => {
          setCaps(before);
          toast.error(`Couldn't save: ${(e as Error).message}`);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <CapabilityCard
        icon={<LightningIcon size={18} />}
        name="EDDY's capabilities while building"
        description="One switch for all three below. On turns every one of them on."
        enabled={masterOn(caps)}
        statusLabel={masterStatus(caps)}
        pending={setCapability.isPending}
        onToggle={() => save(masterToggle(caps))}
      />
      <div className="flex flex-col gap-2 pl-4">
        {BUILDER_CAPABILITIES.map(({ key, name, description }) => (
          <CapabilityCard
            key={key}
            icon={ICONS[key]}
            name={name}
            description={description}
            enabled={caps[key]}
            statusLabel={caps[key] ? 'On' : 'Off'}
            pending={setCapability.isPending}
            onToggle={(on) => save({ ...caps, [key]: on })}
          />
        ))}
      </div>
    </div>
  );
}
